/**
 * ChainMap — 入口组件
 *
 * 数据从 dataStore 读取（App.tsx 顶层加载一次），selectedNode 从 selectionStore 读取。
 * 桌面端：左图谱 + 右详情；移动端：上下分屏。
 */

import { useCallback, useMemo, useState, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { isTollStation } from '../utils/chain-helpers';
import { QUADRANT_DOT_COLORS } from '../utils/chain-constants';
import type { ChainNode, Company, Ranking } from '../types';
import SkillTreeMap from './skilltree/SkillTreeMap';
import DetailPanel from './skilltree/DetailPanel';
import { useDataStore } from '../stores/dataStore';
import { useSelectionStore } from '../stores/selectionStore';

interface ChainMapProps {
  chain: string;
}

export default function ChainMap({ chain }: ChainMapProps) {
  const isMobile = useIsMobile();

  // 从全局 store 读数据（App.tsx 已加载）
  const { nodes, edges, companies, financials, rankings, nodeIndex, loaded } = useDataStore();

  // 从全局 store 读选中态
  const selectedNode = useSelectionStore(s => s.selectedNodeId);
  const selectedCompanyId = useSelectionStore(s => s.selectedCompanyId);
  const selectNode = useSelectionStore(s => s.selectNode);
  const clearSelection = useSelectionStore(s => s.clearSelection);

  const selectedNodeData = selectedNode
    ? nodeIndex.get(selectedNode) ?? null
    : null;

  const onSelectNode = useCallback((id: string | null) => {
    selectNode(id);
  }, [selectNode]);

  // 移动端抽屉展开状态：选中节点时默认 peek（收起），用户可展开
  const [sheetExpanded, setSheetExpanded] = useState(false);
  // 切换节点时重置为 peek — React 19 渲染期同步 setState 模式（官方推荐：派生 state）
  const [prevSelectedNode, setPrevSelectedNode] = useState(selectedNode ?? null);
  if (selectedNode !== prevSelectedNode) {
    setPrevSelectedNode(selectedNode);
    setSheetExpanded(false);
  }

  // drag 手势：向上拖拽展开 / 向下拖拽收起或关闭
  const dragStartY = useRef<number | null>(null);
  const DRAG_THRESHOLD = 40; // px

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current == null) return;
    const endY = e.changedTouches[0].clientY;
    const delta = dragStartY.current - endY; // 正=向上拖，负=向下拖
    dragStartY.current = null;

    if (Math.abs(delta) < DRAG_THRESHOLD) return;

    if (delta > 0) {
      // 向上拖 → 展开
      if (!sheetExpanded) setSheetExpanded(true);
    } else {
      // 向下拖 → 仅 expanded 收起为 peek；peek 状态无响应（关闭走 ✕ 按钮）
      if (sheetExpanded) setSheetExpanded(false);
    }
  }, [sheetExpanded]);

  if (!loaded) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>产业链地图 - 加载中…</div>;
  }

  // 移动端：地图全屏 + 底部抽屉（peek/expand 两态）
  if (isMobile) {
    const sheetHeight = sheetExpanded ? '82vh' : selectedNodeData ? '38vh' : '26vh';
    return (
      <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
        {/* 地图全屏 */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <SkillTreeMap
            allNodes={nodes}
            allEdges={edges}
            chain={chain}
            onSelectNode={onSelectNode}
            selectedNode={selectedNode}
            selectedCompanyId={selectedCompanyId}
            financials={financials}
            rankings={rankings}
            companies={companies}
          />
        </div>

        {/* 底部抽屉 */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: sheetHeight,
          background: '#0f172a', color: '#cbd5e1',
          borderTop: '1px solid #1e293b',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column',
          transition: 'height 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
          zIndex: 10,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {/* 抽屉把手 + 操作区 — 支持 drag 手势 */}
          <div
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
              display: 'flex', alignItems: 'center',
              padding: '6px 12px', flexShrink: 0,
              borderBottom: '1px solid #1e293b',
              gap: 8,
              touchAction: 'pan-x', // 允许水平 pan，垂直交给手势处理
            }}
          >
            {/* ✕ 关闭按钮（左侧，拇指可达） */}
            {selectedNodeData ? (
              <button
                onClick={() => clearSelection()}
                aria-label="关闭详情"
                style={{
                  background: 'transparent', border: 'none', color: '#64748b',
                  cursor: 'pointer', fontSize: 14, padding: '4px 8px',
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            ) : null}
            {/* 把手 — 唯一的 toggle 触发区 */}
            <div
              onClick={() => selectedNodeData && setSheetExpanded(!sheetExpanded)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                cursor: selectedNodeData ? 'pointer' : 'default',
                minWidth: 0,
              }}
            >
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: '#475569', flexShrink: 0,
              }} />
              <div style={{
                fontSize: 11, color: '#94a3b8',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {selectedNodeData ? selectedNodeData.name : '投资机会概览'}
              </div>
            </div>
          </div>

          {/* 抽屉内容 — peek 用精简视图，expanded 用完整 DetailPanel */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {selectedNodeData ? (
              sheetExpanded ? (
                <DetailPanel
                  key={selectedNodeData.id}
                  node={selectedNodeData}
                  allNodes={nodes}
                  allEdges={edges}
                  allCompanies={companies}
                  allFinancials={financials}
                  allRankings={rankings}
                />
              ) : (
                <PeekPanel
                  node={selectedNodeData}
                  allCompanies={companies}
                  allRankings={rankings}
                  onExpand={() => setSheetExpanded(true)}
                />
              )
            ) : (
              <SummaryPanel
                allNodes={nodes.filter(n => chain === 'all' || n.chains.includes(chain))}
                allCompanies={companies}
                allRankings={rankings}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // 桌面端
  return (
    <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* 左侧：技能树地图 70% */}
        <div style={{ flex: 7, position: 'relative' }}>
          <SkillTreeMap
            allNodes={nodes}
            allEdges={edges}
            chain={chain}
            onSelectNode={onSelectNode}
            selectedNode={selectedNode}
            selectedCompanyId={selectedCompanyId}
            financials={financials}
            rankings={rankings}
            companies={companies}
          />
        </div>

        {/* 右侧：详情面板 30% */}
        <div style={{
          flex: 3, borderLeft: '1px solid #1e293b', overflow: 'auto',
          background: '#0f172a', color: '#cbd5e1',
        }}>
          {selectedNodeData ? (
            <DetailPanel
              key={selectedNodeData.id}
              node={selectedNodeData}
              allNodes={nodes}
              allEdges={edges}
              allCompanies={companies}
              allFinancials={financials}
              allRankings={rankings}
            />
          ) : (
            <SummaryPanel
              allNodes={nodes.filter(n => chain === 'all' || n.chains.includes(chain))}
              allCompanies={companies}
              allRankings={rankings}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ===== PeekPanel — 抽屉收起时的精简视图（标题 + 瓶颈一句话 + Top3 公司） =====

function PeekPanel({
  node, allCompanies, allRankings, onExpand,
}: {
  node: ChainNode;
  allCompanies: Company[];
  allRankings: Ranking[];
  onExpand: () => void;
}) {
  // 关联公司按 IPS 排序，可投资优先
  const topCompanies = useMemo(() => {
    const ipsMap = new Map<string, number>();
    for (const r of allRankings) {
      if (r.ips_score != null) ipsMap.set(r.company_id, r.ips_score);
    }
    const list = allCompanies
      .filter(c => node.companies.includes(c.company_id))
      .map(c => ({
        company: c,
        ips: ipsMap.get(c.company_id) ?? c.ips_score ?? 0,
        investable: c.cn_access_level === 'direct' || c.cn_access_level === 'indirect',
      }))
      .sort((a, b) => {
        if (a.investable && !b.investable) return -1;
        if (!a.investable && b.investable) return 1;
        return b.ips - a.ips;
      })
      .slice(0, 3);
    return list;
  }, [node.companies, allCompanies, allRankings]);

  return (
    <div
      onClick={onExpand}
      style={{ padding: '10px 14px', cursor: 'pointer', height: '100%' }}
    >
      {/* tier badge + 瓶颈原因 — 1-2 行截断 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
        <span style={{
          flexShrink: 0, padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
          background: node.bottleneck_level === 'high' ? '#ef4444' : node.bottleneck_level === 'medium' ? '#fbbf24' : '#64748b',
          color: 'white',
        }}>
          {node.bottleneck_level === 'high' ? '高瓶颈' : node.bottleneck_level === 'medium' ? '中瓶颈' : '低瓶颈'}
        </span>
        {node.bottleneck_reason && (
          <div style={{
            flex: 1, fontSize: 11, color: '#fbbf24', lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {node.bottleneck_reason}
          </div>
        )}
      </div>

      {/* Top 3 公司 — 紧凑横排，IPS 颜色梯度（高=金/中=蓝/低=灰） */}
      {topCompanies.length > 0 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {topCompanies.map(({ company: c, ips, investable }) => (
            <div
              key={c.company_id}
              onClick={(e) => { e.stopPropagation(); onExpand(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '3px 7px', borderRadius: 4,
                background: investable ? 'rgba(34,197,94,0.1)' : 'rgba(100,116,139,0.1)',
                border: `1px solid ${investable ? 'rgba(34,197,94,0.3)' : 'rgba(100,116,139,0.2)'}`,
                fontSize: 10,
              }}
            >
              {investable && <span style={{ color: '#22c55e' }}>●</span>}
              <span style={{ color: '#e2e8f0', fontWeight: 600, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.company}
              </span>
              {ips > 0 && (
                <span style={{
                  fontWeight: 700,
                  color: ips >= 70 ? '#fbbf24' : ips >= 50 ? '#60a5fa' : '#94a3b8',
                }}>
                  {ips.toFixed(0)}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>暂无关联公司</div>
      )}

      {/* 展开提示 — bounce 3 次后停止，避免无限循环烦人 */}
      <div style={{
        marginTop: 8, fontSize: 10, color: '#475569',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{ animation: 'bounce 1.5s ease-in-out 3' }}>▲</span>
        <span>展开查看完整详情</span>
      </div>
    </div>
  );
}

// ===== SummaryPanel — 投资机会面板（未选中节点时显示） =====

function SummaryPanel({
  allNodes, allCompanies, allRankings,
}: {
  allNodes: ChainNode[];
  allCompanies: Company[];
  allRankings: Ranking[];
}) {
  const stats = useMemo(() => {
    // IPS map
    const ipsMap = new Map<string, number>();
    for (const r of allRankings) {
      if (r.ips_score != null) ipsMap.set(r.company_id, r.ips_score);
    }
    for (const c of allCompanies) {
      if (c.ips_score != null && !ipsMap.has(c.company_id)) ipsMap.set(c.company_id, c.ips_score);
    }
    // 可投资 map
    const investableMap = new Map<string, boolean>();
    for (const c of allCompanies) {
      investableMap.set(c.company_id, c.cn_access_level === 'direct' || c.cn_access_level === 'indirect');
    }

    // 节点投资吸引力
    const nodeScores = allNodes.map(n => {
      const comps = n.companies ?? [];
      const ipsValues = comps.map(id => ipsMap.get(id)).filter((v): v is number => v != null);
      const avgIps = ipsValues.length > 0 ? ipsValues.reduce((a, b) => a + b, 0) / ipsValues.length : 0;
      const hasInvestable = comps.some(id => investableMap.get(id));
      const toll = isTollStation(n);
      const attractiveness: 'high' | 'medium' | 'low' =
        toll || avgIps >= 70 ? 'high' : avgIps >= 50 ? 'medium' : 'low';
      // 找该节点 IPS 最高的公司
      const topCompany = comps
        .map(id => ({ company: allCompanies.find(c => c.company_id === id), ips: ipsMap.get(id) ?? 0 }))
        .sort((a, b) => b.ips - a.ips)[0];
      return { node: n, avgIps, hasInvestable, attractiveness, topCompany };
    });

    const highAttract = nodeScores.filter(s => s.attractiveness === 'high')
      .sort((a, b) => b.avgIps - a.avgIps).slice(0, 6);
    const highBottleneck = allNodes.filter(n => n.bottleneck_level === 'high');
    const tollStations = allNodes.filter(n => isTollStation(n));
    const topInvestable = [...allRankings]
      .filter(r => investableMap.get(r.company_id))
      .sort((a, b) => (b.ips_score ?? 0) - (a.ips_score ?? 0))
      .slice(0, 8)
      .map(r => ({ ranking: r, company: allCompanies.find(c => c.company_id === r.company_id) }));

    return { highAttract, highBottleneck, tollStations, topInvestable };
  }, [allNodes, allCompanies, allRankings]);

  return (
    <div style={{ padding: 14, color: '#cbd5e1' }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 14, color: '#f1f5f9' }}>投资机会概览</h3>

      {/* 统计 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
        <StatBox label="节点" value={allNodes.length} />
        <StatBox label="公司" value={allCompanies.length} />
        <StatBox label="高瓶颈" value={stats.highBottleneck.length} color="#ef4444" />
        <StatBox label="收费站" value={stats.tollStations.length} color="#fbbf24" />
      </div>

      {/* 投资机会 — 高吸引力节点 */}
      {stats.highAttract.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 5, fontWeight: 600 }}>
            投资机会 — 高吸引力节点
          </div>
          {stats.highAttract.map(({ node: n, avgIps, hasInvestable, topCompany }) => (
            <div key={n.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '4px 0', borderBottom: '1px solid #1e293b', fontSize: 11,
            }}>
              <span style={{ color: '#e2e8f0' }}>
                {hasInvestable && <span style={{ color: '#22c55e', marginRight: 3 }}>●</span>}
                {n.name}
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {topCompany?.company && (
                  <span style={{ color: '#64748b', fontSize: 10 }}>{topCompany.company.company}</span>
                )}
                {avgIps > 0 && (
                  <span style={{
                    color: avgIps >= 70 ? '#fbbf24' : '#60a5fa',
                    fontWeight: 600, fontSize: 10,
                  }}>{avgIps.toFixed(0)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 可投资标的 — Top IPS */}
      {stats.topInvestable.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: '#22c55e', marginBottom: 5, fontWeight: 600 }}>
            可投资标的 — Top IPS
          </div>
          {stats.topInvestable.map(({ ranking: r, company: c }) => {
            if (!c) return null;
            const ips = r.ips_score ?? 0;
            return (
              <div key={r.company_id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '3px 0', borderBottom: '1px solid #1e293b', fontSize: 11,
              }}>
                <span style={{ color: '#e2e8f0' }}>
                  <span style={{ color: '#22c55e', marginRight: 3 }}>●</span>
                  {c.company}
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontSize: 10 }}>{c.ticker}</span>
                  {r.quadrant && (
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: QUADRANT_DOT_COLORS[r.quadrant] || '#64748b',
                      display: 'inline-block',
                    }} />
                  )}
                  <span style={{
                    color: ips >= 70 ? '#fbbf24' : ips >= 50 ? '#60a5fa' : '#64748b',
                    fontWeight: 600, fontSize: 10, minWidth: 24, textAlign: 'right',
                  }}>{ips.toFixed(0)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color = '#f1f5f9' }: { label: string; value: number; color?: string }) {
  return (
    <div style={{ padding: 10, borderRadius: 6, background: '#0b1220', border: '1px solid #1e293b' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748b' }}>{label}</div>
    </div>
  );
}
